import { useEffect, useMemo, useState } from "react";
import SBINModal from "../modals/SBINModal";
import {
  fetchSuperAdminMarketplaceBoothById,
  fetchSuperAdminMarketplaceBooths,
  fetchSuperAdminMarketplaceOverview,
  fetchSuperAdminMarketplaceOwners,
  fetchSuperAdminMarketplaceReadiness,
  fetchSuperAdminMarketplaceSalonsWithoutBooths,
  updateSuperAdminMarketplaceBoothAvailability,
} from "../../api/superadmin";

function formatDateTime(value) {
  if (!value) return "--";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "--";
  return parsed.toLocaleString();
}

function formatNames(items = [], key) {
  if (!Array.isArray(items) || items.length === 0) return "--";
  return items
    .map((item) => item?.[key])
    .filter(Boolean)
    .join(", ");
}

function ReadinessPill({ readiness }) {
  const isReady = Boolean(readiness?.is_marketplace_ready);
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
        isReady ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
      }`}
    >
      {isReady ? "Ready" : "Needs attention"}
    </span>
  );
}

export default function SuperAdminMarketplaceSection({
  businesses,
  allSalons,
  selectedBusinessId,
  onChangeBusinessId,
  onInspectSalon,
  inspectSalonLabel = "Filter salons",
}) {
  const [selectedSalonId, setSelectedSalonId] = useState("");
  const [boothSearch, setBoothSearch] = useState("");
  const [ownerSearch, setOwnerSearch] = useState("");
  const [availability, setAvailability] = useState("all");
  const [inventoryMode, setInventoryMode] = useState("all");

  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState("");

  const [booths, setBooths] = useState([]);
  const [boothsTotal, setBoothsTotal] = useState(0);
  const [boothsLoading, setBoothsLoading] = useState(false);
  const [boothsError, setBoothsError] = useState("");

  const [readinessRows, setReadinessRows] = useState([]);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessError, setReadinessError] = useState("");

  const [owners, setOwners] = useState([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [ownersError, setOwnersError] = useState("");

  const [salonsWithoutBooths, setSalonsWithoutBooths] = useState([]);
  const [salonsWithoutBoothsLoading, setSalonsWithoutBoothsLoading] = useState(false);
  const [salonsWithoutBoothsError, setSalonsWithoutBoothsError] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailNotice, setDetailNotice] = useState("");
  const [selectedBoothDetail, setSelectedBoothDetail] = useState(null);
  const [availabilityReason, setAvailabilityReason] = useState("");
  const [availabilitySubmitting, setAvailabilitySubmitting] = useState(false);

  const [reloadNonce, setReloadNonce] = useState(0);

  const businessFilterValue =
    selectedBusinessId != null && selectedBusinessId !== "" ? Number(selectedBusinessId) : "";

  const salonOptions = useMemo(() => {
    const rows = Array.isArray(allSalons) ? allSalons : [];
    if (!businessFilterValue) return rows;
    return rows.filter(
      (salon) => Number(salon.business_id || 0) === Number(businessFilterValue)
    );
  }, [allSalons, businessFilterValue]);

  useEffect(() => {
    if (!selectedSalonId) return;
    const exists = salonOptions.some((salon) => String(salon.id) === String(selectedSalonId));
    if (!exists) {
      setSelectedSalonId("");
    }
  }, [selectedSalonId, salonOptions]);

  useEffect(() => {
    let mounted = true;
    setOverviewLoading(true);
    setOverviewError("");
    fetchSuperAdminMarketplaceOverview()
      .then((data) => {
        if (!mounted) return;
        setOverview(data || null);
      })
      .catch((err) => {
        if (!mounted) return;
        setOverviewError(err?.message || "Unable to load marketplace overview.");
        setOverview(null);
      })
      .finally(() => {
        if (mounted) setOverviewLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [reloadNonce]);

  useEffect(() => {
    let mounted = true;
    setSalonsWithoutBoothsLoading(true);
    setSalonsWithoutBoothsError("");
    fetchSuperAdminMarketplaceSalonsWithoutBooths()
      .then((data) => {
        if (!mounted) return;
        setSalonsWithoutBooths(Array.isArray(data?.items) ? data.items : []);
      })
      .catch((err) => {
        if (!mounted) return;
        setSalonsWithoutBoothsError(err?.message || "Unable to load salons without booths.");
        setSalonsWithoutBooths([]);
      })
      .finally(() => {
        if (mounted) setSalonsWithoutBoothsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [reloadNonce]);

  useEffect(() => {
    let mounted = true;
    setBoothsLoading(true);
    setBoothsError("");
    fetchSuperAdminMarketplaceBooths({
      business_id: businessFilterValue || undefined,
      salon_id: selectedSalonId || undefined,
      q: boothSearch.trim() || undefined,
      availability,
      limit: 200,
    })
      .then((data) => {
        if (!mounted) return;
        setBooths(Array.isArray(data?.items) ? data.items : []);
        setBoothsTotal(Number(data?.total || 0));
      })
      .catch((err) => {
        if (!mounted) return;
        setBoothsError(err?.message || "Unable to load booth inventory.");
        setBooths([]);
        setBoothsTotal(0);
      })
      .finally(() => {
        if (mounted) setBoothsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [availability, boothSearch, businessFilterValue, reloadNonce, selectedSalonId]);

  useEffect(() => {
    let mounted = true;
    setReadinessLoading(true);
    setReadinessError("");
    fetchSuperAdminMarketplaceReadiness({
      business_id: businessFilterValue || undefined,
      salon_id: selectedSalonId || undefined,
      only_not_ready: false,
    })
      .then((data) => {
        if (!mounted) return;
        setReadinessRows(Array.isArray(data?.items) ? data.items : []);
      })
      .catch((err) => {
        if (!mounted) return;
        setReadinessError(err?.message || "Unable to load readiness.");
        setReadinessRows([]);
      })
      .finally(() => {
        if (mounted) setReadinessLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [businessFilterValue, reloadNonce, selectedSalonId]);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setOwnersLoading(true);
      setOwnersError("");
      fetchSuperAdminMarketplaceOwners({
        business_id: businessFilterValue || undefined,
        salon_id: selectedSalonId || undefined,
        q: ownerSearch.trim() || undefined,
      })
        .then((data) => {
          if (cancelled) return;
          setOwners(Array.isArray(data?.items) ? data.items : []);
        })
        .catch((err) => {
          if (cancelled) return;
          setOwnersError(err?.message || "Unable to load owners.");
          setOwners([]);
        })
        .finally(() => {
          if (!cancelled) setOwnersLoading(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [businessFilterValue, ownerSearch, reloadNonce, selectedSalonId]);

  const visibleBooths = useMemo(() => {
    const rows = Array.isArray(booths) ? booths : [];
    if (inventoryMode !== "not_ready") return rows;
    return rows.filter((row) => !row?.readiness?.is_marketplace_ready);
  }, [booths, inventoryMode]);

  const visibleSalonsWithoutBooths = useMemo(() => {
    const rows = Array.isArray(salonsWithoutBooths) ? salonsWithoutBooths : [];
    if (!businessFilterValue) return rows;
    return rows.filter(
      (row) => Number(row.business_id || 0) === Number(businessFilterValue)
    );
  }, [businessFilterValue, salonsWithoutBooths]);

  const openBoothDetail = async (boothId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError("");
    setDetailNotice("");
    setAvailabilityReason("");
    try {
      const data = await fetchSuperAdminMarketplaceBoothById(boothId);
      setSelectedBoothDetail(data || null);
    } catch (err) {
      setDetailError(err?.message || "Unable to load booth detail.");
      setSelectedBoothDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeBoothDetail = () => {
    if (availabilitySubmitting) return;
    setDetailOpen(false);
    setSelectedBoothDetail(null);
    setDetailError("");
    setDetailNotice("");
    setAvailabilityReason("");
  };

  const handleAvailabilityToggle = async () => {
    const boothId = selectedBoothDetail?.id;
    const reason = availabilityReason.trim();
    if (!boothId) return;
    if (!reason) {
      setDetailError("Reason is required for super-admin availability changes.");
      return;
    }
    setAvailabilitySubmitting(true);
    setDetailError("");
    setDetailNotice("");
    try {
      await updateSuperAdminMarketplaceBoothAvailability(boothId, {
        is_available: !selectedBoothDetail?.is_available,
        reason,
      });
      const refreshed = await fetchSuperAdminMarketplaceBoothById(boothId);
      setSelectedBoothDetail(refreshed || null);
      setAvailabilityReason("");
      setDetailNotice("Availability updated and audited.");
      setReloadNonce((value) => value + 1);
    } catch (err) {
      setDetailError(err?.message || "Unable to update booth availability.");
    } finally {
      setAvailabilitySubmitting(false);
    }
  };

  return (
    <section
      id="sa-marketplace"
      className="rounded-2xl border border-slate-200 bg-white p-4 space-y-5"
    >
      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Marketplace</h2>
          <p className="text-sm text-slate-500">
            Platform-wide booth inventory, readiness, and host visibility inside the existing
            Super Admin console.
          </p>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Business
            </label>
            <select
              className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm"
              value={businessFilterValue}
              onChange={(event) => {
                const nextValue = event.target.value ? Number(event.target.value) : null;
                onChangeBusinessId(nextValue);
                setSelectedSalonId("");
              }}
            >
              <option value="">All businesses</option>
              {(businesses || []).map((business) => (
                <option key={business.id} value={business.id}>
                  Business ID {business.id} - {business.name || "(Unnamed business)"}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Salon
            </label>
            <select
              className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm"
              value={selectedSalonId}
              onChange={(event) => setSelectedSalonId(event.target.value)}
            >
              <option value="">All salons</option>
              {salonOptions.map((salon) => (
                <option key={salon.id} value={salon.id}>
                  Salon ID {salon.id} - {salon.name || "(Unnamed salon)"}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Availability
            </label>
            <select
              className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm"
              value={availability}
              onChange={(event) => setAvailability(event.target.value)}
            >
              <option value="all">All</option>
              <option value="available">Available</option>
              <option value="unavailable">Unavailable</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Search
            </label>
            <input
              type="text"
              value={boothSearch}
              onChange={(event) => setBoothSearch(event.target.value)}
              placeholder="Booth, salon, owner"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-300"
            />
          </div>
        </div>
      </div>

      {overviewError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {overviewError}
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            label: "Businesses with booths",
            value: overview?.businesses_with_booths,
          },
          {
            label: "Salons with booths",
            value: overview?.salons_with_booths,
          },
          {
            label: "Booths ready",
            value:
              overview && overview.total_booths != null && overview.booths_not_ready != null
                ? Number(overview.total_booths) - Number(overview.booths_not_ready)
                : null,
          },
          {
            label: "Booths needing attention",
            value: overview?.booths_not_ready,
          },
        ].map((card) => (
          <div
            key={card.label}
            className="rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {card.label}
            </p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">
              {overviewLoading ? "..." : card.value ?? "--"}
            </p>
          </div>
        ))}
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.4fr,1fr]">
        <div className="rounded-2xl border border-slate-200">
          <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Marketplace Booth Inventory</h3>
              <p className="text-sm text-slate-500">
                {boothsLoading ? "Loading..." : `${visibleBooths.length} of ${boothsTotal} rows`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setInventoryMode("all")}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                  inventoryMode === "all"
                    ? "bg-slate-900 text-white"
                    : "border border-slate-300 bg-white text-slate-700"
                }`}
              >
                All booths
              </button>
              <button
                type="button"
                onClick={() => setInventoryMode("not_ready")}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                  inventoryMode === "not_ready"
                    ? "bg-amber-500 text-white"
                    : "border border-slate-300 bg-white text-slate-700"
                }`}
              >
                Not ready only
              </button>
            </div>
          </div>
          {boothsError ? (
            <div className="px-4 py-3 text-sm text-rose-700">{boothsError}</div>
          ) : null}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100/80 text-left text-slate-700">
                <tr>
                  <th className="px-3 py-2.5 font-semibold">Workspace</th>
                  <th className="px-3 py-2.5 font-semibold">Salon</th>
                  <th className="px-3 py-2.5 font-semibold">Owner</th>
                  <th className="px-3 py-2.5 font-semibold">Availability</th>
                  <th className="px-3 py-2.5 font-semibold">Readiness</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {boothsLoading ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                      Loading marketplace booth inventory...
                    </td>
                  </tr>
                ) : visibleBooths.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                      No booths match the current marketplace filters.
                    </td>
                  </tr>
                ) : (
                  visibleBooths.map((booth) => (
                    <tr key={booth.id} className="border-t border-slate-200">
                      <td className="px-3 py-2.5">
                        <button
                          type="button"
                          onClick={() => openBoothDetail(booth.id)}
                          className="text-left"
                        >
                          <span className="block font-semibold text-slate-900">
                            {booth.booth_name || "Unnamed booth"}
                          </span>
                          <span className="block text-xs text-slate-500">
                            {booth.booth_number || "No code"} | ${booth.price_per_hour || 0}/hr
                          </span>
                        </button>
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">
                        <div>{booth.salon_name || "--"}</div>
                        <div className="text-xs text-slate-500">{booth.business_name || "--"}</div>
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">
                        {booth.owner_full_name || booth.owner_username || booth.owner_email || "--"}
                      </td>
                      <td className="px-3 py-2.5">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                            booth.is_available
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {booth.is_available ? "Available" : "Unavailable"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <ReadinessPill readiness={booth.readiness} />
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <button
                          type="button"
                          onClick={() => openBoothDetail(booth.id)}
                          className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          View Detail
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Marketplace Readiness</h3>
              <p className="text-sm text-slate-500">
                Worst operational gaps surface first.
              </p>
            </div>
          </div>
          {readinessError ? (
            <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {readinessError}
            </div>
          ) : null}
          <div className="mt-4 space-y-3">
            {readinessLoading ? (
              <p className="text-sm text-slate-500">Loading readiness...</p>
            ) : readinessRows.length === 0 ? (
              <p className="text-sm text-slate-500">No readiness rows available.</p>
            ) : (
              readinessRows.slice(0, 10).map((row) => (
                <div key={`${row.business_id}-${row.salon_id}`} className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{row.salon_name}</p>
                      <p className="text-xs text-slate-500">{row.business_name || "Unassigned business"}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (row.business_id) {
                          onChangeBusinessId(row.business_id);
                        }
                        setSelectedSalonId(row.salon_id ? String(row.salon_id) : "");
                        setInventoryMode("not_ready");
                      }}
                      className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100"
                    >
                      View not ready booths
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                    <span className="rounded-full bg-white px-2 py-1">
                      Booths: {row.booth_count}
                    </span>
                    <span className="rounded-full bg-white px-2 py-1">
                      Ready: {row.ready_booth_count}
                    </span>
                    <span className="rounded-full bg-white px-2 py-1">
                      Not ready: {row.not_ready_booth_count}
                    </span>
                    {row.no_booth_count ? (
                      <span className="rounded-full bg-white px-2 py-1">No booths</span>
                    ) : null}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(row.reasons || []).length === 0 ? (
                      <span className="text-xs text-slate-500">No major data gaps.</span>
                    ) : (
                      row.reasons.slice(0, 4).map((reason) => (
                        <span
                          key={`${row.salon_id}-${reason.code}`}
                          className="rounded-full bg-amber-100 px-2 py-1 text-xs text-amber-700"
                        >
                          {reason.code.replaceAll("_", " ")} ({reason.count})
                        </span>
                      ))
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.2fr,1fr]">
        <div className="rounded-2xl border border-slate-200 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-base font-semibold text-slate-900">Marketplace Owners</h3>
              <p className="text-sm text-slate-500">
                Host, admin, and owner-linked users for support and operations.
              </p>
            </div>
            <input
              type="text"
              value={ownerSearch}
              onChange={(event) => setOwnerSearch(event.target.value)}
              placeholder="Search owners"
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-300"
            />
          </div>
          {ownersError ? (
            <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {ownersError}
            </div>
          ) : null}
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100/80 text-left text-slate-700">
                <tr>
                  <th className="px-3 py-2.5 font-semibold">User</th>
                  <th className="px-3 py-2.5 font-semibold">Booths</th>
                  <th className="px-3 py-2.5 font-semibold">Businesses</th>
                  <th className="px-3 py-2.5 font-semibold">Salons</th>
                </tr>
              </thead>
              <tbody>
                {ownersLoading ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                      Loading owners...
                    </td>
                  </tr>
                ) : owners.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                      No owners match the current filters.
                    </td>
                  </tr>
                ) : (
                  owners.map((owner) => (
                    <tr key={owner.id} className="border-t border-slate-200">
                      <td className="px-3 py-2.5 text-slate-700">
                        <div className="font-semibold text-slate-900">
                          {owner.full_name || owner.username || owner.email || `User ${owner.id}`}
                        </div>
                        <div className="text-xs text-slate-500">{owner.email || "--"}</div>
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">{owner.booth_count}</td>
                      <td className="px-3 py-2.5 text-slate-700">
                        {formatNames(owner.linked_businesses, "business_name")}
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">
                        {formatNames(owner.linked_salons, "salon_name")}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 p-4">
          <h3 className="text-base font-semibold text-slate-900">Salons Without Booths</h3>
          <p className="text-sm text-slate-500">
            Active salons that still have no marketplace inventory.
          </p>
          {salonsWithoutBoothsError ? (
            <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {salonsWithoutBoothsError}
            </div>
          ) : null}
          <div className="mt-4 space-y-3">
            {salonsWithoutBoothsLoading ? (
              <p className="text-sm text-slate-500">Loading salons...</p>
            ) : visibleSalonsWithoutBooths.length === 0 ? (
              <p className="text-sm text-slate-500">No active salons without booths in scope.</p>
            ) : (
              visibleSalonsWithoutBooths.slice(0, 12).map((row) => (
                <div key={row.salon_id} className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{row.salon_name}</p>
                      <p className="text-xs text-slate-500">
                        {row.business_name || "Unassigned business"} | {row.city || "--"},{" "}
                        {row.province || "--"}
                      </p>
                    </div>
                    {onInspectSalon ? (
                      <button
                        type="button"
                        onClick={() =>
                          onInspectSalon({
                            businessId: row.business_id,
                            salonId: row.salon_id,
                          })
                        }
                        className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        {inspectSalonLabel}
                      </button>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <SBINModal
        isOpen={detailOpen}
        onClose={closeBoothDetail}
        title="Marketplace Booth Detail"
        size="md"
        fitContent
        closeOnBackdrop
      >
        <div className="space-y-4">
          {detailLoading ? (
            <p className="text-sm text-slate-500">Loading booth detail...</p>
          ) : null}
          {detailError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {detailError}
            </div>
          ) : null}
          {detailNotice ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {detailNotice}
            </div>
          ) : null}
          {!detailLoading && selectedBoothDetail ? (
            <>
              <div>
                <p className="text-lg font-semibold text-slate-900">
                  {selectedBoothDetail.booth_name || "Unnamed booth"}
                </p>
                <p className="text-sm text-slate-500">
                  {selectedBoothDetail.business?.name || "--"} /{" "}
                  {selectedBoothDetail.salon?.name || "--"}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Availability
                  </p>
                  <p className="mt-2 text-sm text-slate-900">
                    {selectedBoothDetail.is_available ? "Available" : "Unavailable"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Hours: {selectedBoothDetail.available_start_time || "--"} to{" "}
                    {selectedBoothDetail.available_end_time || "--"}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Readiness
                  </p>
                  <div className="mt-2">
                    <ReadinessPill readiness={selectedBoothDetail.readiness} />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    Photos: {selectedBoothDetail.photos?.length || 0} | Add-ons:{" "}
                    {selectedBoothDetail.amenity_addons?.length || 0}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Owner
                  </p>
                  <p className="mt-2 text-sm text-slate-900">
                    {selectedBoothDetail.owner?.full_name ||
                      selectedBoothDetail.owner?.username ||
                      selectedBoothDetail.owner?.email ||
                      "--"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {selectedBoothDetail.owner?.email || "--"}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Last updated
                  </p>
                  <p className="mt-2 text-sm text-slate-900">
                    {formatDateTime(selectedBoothDetail.updated_at)}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Created: {formatDateTime(selectedBoothDetail.created_at)}
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3">
                <p className="text-sm font-semibold text-amber-800">
                  Super-admin-only action
                </p>
                <p className="mt-1 text-sm text-amber-700">
                  Changing booth availability here overrides the normal salon management flow.
                  A reason is required and the action is audited.
                </p>
                <textarea
                  value={availabilityReason}
                  onChange={(event) => setAvailabilityReason(event.target.value)}
                  placeholder="Enter the operational reason for this availability change"
                  className="mt-3 min-h-[88px] w-full rounded-xl border border-amber-300 bg-white px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200"
                />
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={handleAvailabilityToggle}
                    disabled={availabilitySubmitting}
                    className="rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                  >
                    {availabilitySubmitting
                      ? "Saving..."
                      : selectedBoothDetail.is_available
                        ? "Mark unavailable"
                        : "Mark available"}
                  </button>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-900">Diagnostics</h4>
                {(selectedBoothDetail.diagnostics || []).length === 0 ? (
                  <p className="mt-2 text-sm text-slate-500">No readiness gaps detected.</p>
                ) : (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedBoothDetail.diagnostics.map((reason) => (
                      <span
                        key={reason}
                        className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700"
                      >
                        {String(reason).replaceAll("_", " ")}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </SBINModal>
    </section>
  );
}
